import { css } from '@emotion/css';
import walletApi from 'apis/walletApi';
import classNames from 'classnames';
import { colors } from 'constants/theme';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { NumericFormat } from 'react-number-format';
import LoadingIcon from 'components/loading/LoadingIcon';
import authApi from 'apis/authApi';

interface UsdtWallet {
    id: number;
    wallet_address: string;
    coin_symbol: string;
    network: string;
    status: number;
    is_default: number;
    loginname: string;
    label: string | null;
}

interface UsdtRate {
    provider_code: string;
    coin_symbol: string;
    rate_in: number;
    rate_out: number;
    rate: number;
    from_currency: string;
    to_currency: string;
}

const listAmountQuickUSDT = [
    { value: 10, label: '10$' },
    { value: 50, label: '50$' },
    { value: 100, label: '100$' },
    { value: 200, label: '200$' },
    { value: 500, label: '500$' },
    { value: 1000, label: '1000$' },
    { value: 2000, label: '2000$' },
    { value: 5000, label: '5000$' },
];

function UsdtWithdraw() {
    const [withdrawFee, setWithdrawFee] = useState<number>(2);
    const [minWithdraw, setMinWithdraw] = useState<number>(0);
    const [maxWithdraw, setMaxWithdraw] = useState<number>(0);
    const [loading, setLoading] = useState(false);
    const [walletList, setWalletList] = useState<UsdtWallet[]>([]);
    const [selectedWalletId, setSelectedWalletId] = useState<number>(0);
    const [rate, setRate] = useState<UsdtRate | null>(null);
    const [amount, setAmount] = useState('');
    const [passPay, setPassPay] = useState('');

    // Add wallet form
    const [showAddWallet, setShowAddWallet] = useState(false);
    const [newAddress, setNewAddress] = useState('');
    const [addWalletPwd, setAddWalletPwd] = useState('');
    const [addWalletPwd2, setAddWalletPwd2] = useState('');
    const [addingWallet, setAddingWallet] = useState(false);
    const [hasPaymentPwd, setHasPaymentPwd] = useState(true);

    useEffect(() => {
        initData();
    }, []);

    const initData = async () => {
        setLoading(true);
        try {
            const [walletRes, rateRes, bankRes, providerRes] = await Promise.all([
                walletApi.usdtListWithdrawWallet({ coinSymbol: 'USDTTRC20' }),
                walletApi.usdtGetRate({ provider_code: 'MFCPAY', coin_symbol: 'USDTTRC20' }),
                authApi.getUserInfo(),
                walletApi.usdtGetProviders()
            ]);

            // Check if user has existing bank → has payment password
            const hasBanks = bankRes.data.bankcheck > 0;
            setHasPaymentPwd(hasBanks);

            const wallets = walletRes.data.usdtAddresses || walletRes.data.data || [];
            if (wallets.length > 0) {
                setWalletList(wallets);
                setSelectedWalletId(wallets[0].id);
                setShowAddWallet(false);
            } else {
                setShowAddWallet(true);
            }

            if (rateRes.data.data) {
                setRate(rateRes.data.data);
            }

            if (!providerRes.data.msg && providerRes.data.data?.length > 0) {
                setWithdrawFee(Number(providerRes.data.data[0].withdraw_fee) || 2);
                setMinWithdraw(Number(providerRes.data.data[0].min_withdraw) || 0);
                setMaxWithdraw(Number(providerRes.data.data[0].max_withdraw) || 0);
            }
        } catch (error) {
            console.log(error);
            toast.error('Không thể tải thông tin rút USDT');
        } finally {
            setLoading(false);
        }
    };

    const addWallet = async () => {
        if (!newAddress) {
            toast.error('Vui lòng nhập địa chỉ ví USDT');
            return;
        }
        const trc20Regex = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
        if (!trc20Regex.test(newAddress)) {
            toast.error('Địa chỉ ví không hợp lệ. Địa chỉ TRC20 phải bắt đầu bằng chữ "T" và có 34 ký tự');
            return;
        }
        if (!addWalletPwd) {
            toast.error('Vui lòng nhập mật khẩu thanh toán');
            return;
        }
        if (!hasPaymentPwd) {
            if (!addWalletPwd2) {
                toast.error('Vui lòng nhập lại mật khẩu thanh toán');
                return;
            }
            if (addWalletPwd !== addWalletPwd2) {
                toast.error('Nhập lại mật khẩu thanh toán không đúng');
                return;
            }
        }
        setAddingWallet(true);
        try {
            const payload: any = {
                usdtAddress: newAddress,
                checkpwd: addWalletPwd,
                coinSymbol: 'USDTTRC20'
            };
            const { data } = await walletApi.usdtAddWithdrawWallet(payload);
            if (!data.msg) {
                toast.success('Thêm ví USDT thành công!');
                setNewAddress('');
                setAddWalletPwd('');
                setAddWalletPwd2('');
                setShowAddWallet(false);
                await initData();
            } else {
                toast.error(data.msg || 'Không thể thêm ví');
            }
        } catch (error) {
            console.log(error);
            toast.error('Có lỗi xảy ra khi thêm ví');
        } finally {
            setAddingWallet(false);
        }
    };

    const changeAmount = (value: string) => {
        const raw = value.replace(/[,]/g, '');
        if (raw === '') {
            setAmount('');
            return;
        }
        if (isNaN(Number(raw))) return;
        setAmount(raw);
    };

    const selectQuickAmount = (value: number) => {
        setAmount(String(value));
    };

    const calcVND = () => {
        const amountNumber = Number(amount);
        if (rate?.rate_out && amountNumber) {
            return new Intl.NumberFormat().format(amountNumber * rate.rate_out);
        }
        return '';
    };

    const submitWithdraw = async () => {
        if (!selectedWalletId) {
            toast.error('Vui lòng chọn ví USDT nhận tiền');
            return;
        }

        const amountNumber = Number(amount);
        if (!amount || amountNumber <= 0) {
            toast.error('Vui lòng nhập số USDT muốn rút');
            return;
        }

        if (minWithdraw > 0 && amountNumber < minWithdraw) {
            toast.error(`Tối thiểu rút ${minWithdraw} USDT`);
            return;
        }

        if (maxWithdraw > 0 && amountNumber > maxWithdraw) {
            toast.error(`Tối đa rút ${maxWithdraw} USDT`);
            return;
        }

        if (!passPay) {
            toast.error('Vui lòng nhập mật khẩu thanh toán');
            return;
        }

        const wamount = Math.round(amountNumber * (rate?.rate_out || 1));

        setLoading(true);
        try {
            const { data } = await walletApi.usdtWithdraw({
                wamount: String(wamount),
                usdtid: selectedWalletId,
                checkpwd: passPay
            });

            if (!data.msg) {
                toast.success('Gửi yêu cầu rút tiền thành công!');
                setAmount('');
                setPassPay('');
            } else {
                toast.error(data.msg);
            }
        } catch (error) {
            console.log(error);
            toast.error('Có lỗi xảy ra khi rút tiền');
        } finally {
            setLoading(false);
        }
    };

    if (loading && !rate) {
        return <div className={styles.loading}>Đang tải...</div>;
    }

    return (
        <div className={styles.container}>
            {/* Ví USDT nhận tiền */}
            <div className={styles.walletRow}>
                {walletList.length > 0 ? (
                    <>
                        <span className={styles.walletLabel}>Chọn Ví USDT</span>
                        <select className={styles.walletSelect} value={selectedWalletId} onChange={(e) => setSelectedWalletId(Number(e.target.value))}>
                            {walletList.map((w) => (
                                <option key={w.id} value={w.id}>
                                    {w.wallet_address} ({w.network || 'TRC20'})
                                </option>
                            ))}
                        </select>
                        <button type="button" className={styles.addWalletBtn} onClick={() => setShowAddWallet(!showAddWallet)}>
                            {showAddWallet ? '✕ Hủy' : '+ Thêm ví'}
                        </button>
                    </>
                ) : (
                    <div className={styles.noWalletHint}>Chưa liên kết ví USDT nào! Vui lòng thêm ví bên dưới để rút tiền.</div>
                )}
            </div>

            {/* Form thêm ví */}
            {(showAddWallet || walletList.length === 0) && (
                <div className={styles.addWalletForm}>
                    <input
                        className={styles.inputField}
                        type="text"
                        placeholder="Địa chỉ ví TRC20 (VD: T...)"
                        value={newAddress}
                        onChange={(e) => setNewAddress(e.target.value)}
                    />
                    <input
                        className={styles.inputField}
                        type="password"
                        placeholder={hasPaymentPwd ? 'Mật khẩu thanh toán' : 'Tạo mật khẩu thanh toán mới'}
                        value={addWalletPwd}
                        onChange={(e) => setAddWalletPwd(e.target.value)}
                    />
                    {!hasPaymentPwd && (
                        <input
                            className={styles.inputField}
                            type="password"
                            placeholder="Xác nhận mật khẩu thanh toán"
                            value={addWalletPwd2}
                            onChange={(e) => setAddWalletPwd2(e.target.value)}
                        />
                    )}
                    <button type="button" className={styles.submitAddBtn} onClick={addWallet} disabled={addingWallet}>
                        {addingWallet ? 'Đang thêm...' : 'Xác nhận thêm ví'}
                    </button>
                </div>
            )}

            {walletList.length > 0 && (
                <>
                    {/* Số Tiền Rút (USDT) */}
                    <div className={styles.formSection}>
                        <NumericFormat
                            className={styles.inputField}
                            decimalScale={6}
                            placeholder={`Rút tối thiểu ${minWithdraw} - tối đa ${maxWithdraw} USDT`}
                            value={amount}
                            onChange={(e) => changeAmount(e.target.value)}
                        />

                        {/* Info inline giống applux */}
                        <div className={styles.infoLines}>
                            <div className={styles.infoLine}>
                                <span>Rút tối thiểu</span>
                                <span className={styles.infoValue}>{new Intl.NumberFormat().format(minWithdraw)} USDT</span>
                            </div>
                            <div className={styles.infoLine}>
                                <span>Rút tối đa</span>
                                <span className={styles.infoValue}>{new Intl.NumberFormat().format(maxWithdraw)} USDT</span>
                            </div>
                            <div className={styles.infoLine}>
                                <span>Tỉ giá</span>
                                <span className={styles.infoValue}>1 USDT ~ {new Intl.NumberFormat().format(rate?.rate_out || 0)} VNĐ</span>
                            </div>
                            {calcVND() && (
                                <div className={styles.infoLine}>
                                    <span>Số tiền rút</span>
                                    <span className={styles.infoValue}>{calcVND()} VNĐ</span>
                                </div>
                            )}
                            <div className={styles.infoLine}>
                                <span>Phí rút</span>
                                <span className={styles.feeValue}>{new Intl.NumberFormat().format(withdrawFee)} USDT</span>
                            </div>
                            {amount && Number(amount) >= minWithdraw && (
                                <div className={styles.infoLine}>
                                    <span>Số USDT nhận được</span>
                                    <strong className={styles.netValue}>{Number(amount) - withdrawFee > 0 ? Number(amount) - withdrawFee : 0} USDT</strong>
                                </div>
                            )}
                        </div>

                        <input
                            className={styles.inputField}
                            type="password"
                            placeholder="Mật khẩu Thanh Toán"
                            value={passPay}
                            onChange={(e) => setPassPay(e.target.value)}
                        />
                    </div>

                    {/* Quick amounts */}
                    <div className={styles.amountList}>
                        {listAmountQuickUSDT.map((item) => (
                            <div className={styles.amountItem} key={item.value}>
                                <p
                                    className={classNames(styles.amountItemInner, {
                                        active: amount === String(item.value)
                                    })}
                                    onClick={() => selectQuickAmount(item.value)}
                                >
                                    {item.label}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Submit */}
                    <div className={styles.footerRow}>
                        <button
                            type="button"
                            className={styles.submitBtn}
                            onClick={submitWithdraw}
                            disabled={loading}
                        >
                            {loading ? 'Đang xử lý...' : 'RÚT TIỀN'}
                        </button>
                    </div>
                </>
            )}

            {loading && <LoadingIcon />}
        </div>
    );
}

const styles = {
    container: css`
        margin-top: 12px;
        position: relative;
    `,
    loading: css`
        text-align: center;
        padding: 40px;
        color: ${colors.primary};
    `,
    walletRow: css`
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 12px;
        flex-wrap: wrap;
        background: linear-gradient(135deg, #0d2137 0%, #132d4a 100%);
        border: 1px solid #2a4a5a;
        border-radius: 8px;
        padding: 8px 14px;
    `,
    walletLabel: css`
        color: #9fb4c6;
        font-size: 13px;
        font-weight: 600;
        white-space: nowrap;
    `,
    walletSelect: css`
        flex: 1 1 280px;
        min-width: 200px;
        padding: 6px 10px;
        border-radius: 6px;
        border: 1px solid #2a4a5a;
        background: #0f1922;
        color: #fff;
        font-size: 13px;
        outline: none;
        cursor: pointer;
        option {
            background: #0f1922;
            color: #fff;
        }
    `,
    noWalletHint: css`
        flex: 1;
        color: #ffc107;
        font-size: 14px;
        padding: 8px 12px;
        background: rgba(255, 193, 7, 0.1);
        border-left: 3px solid #ffc107;
        border-radius: 4px;
    `,
    addWalletBtn: css`
        background: transparent;
        border: 1px solid #00d4aa;
        color: #00d4aa;
        padding: 8px 14px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 13px;
        font-weight: 500;
        transition: all 0.2s;
        &:hover {
            background: rgba(0, 212, 170, 0.1);
        }
    `,
    addWalletForm: css`
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 12px;
        padding: 12px;
        background: #0f1922;
        border-radius: 8px;
        border: 1px dashed #00d4aa;
    `,
    inputField: css`
        width: 100%;
        padding: 10px 12px;
        border-radius: 6px;
        border: 1px solid #3a4a5a;
        background: #0f1922;
        color: #fff;
        font-size: 14px;
        outline: none;
        transition: border-color 0.2s;
        &::placeholder {
            color: #7a8a9a;
        }
        &:focus {
            border-color: #00d4aa;
        }
    `,
    submitAddBtn: css`
        background: linear-gradient(135deg, #00d4aa 0%, #00a884 100%);
        border: none;
        color: #000;
        padding: 10px 20px;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 600;
        font-size: 14px;
        transition: all 0.2s;
        &:hover {
            box-shadow: 0 2px 10px rgba(0, 212, 170, 0.3);
        }
        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    `,
    formSection: css`
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 16px;
        max-width: 500px;
    `,
    infoLines: css`
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 10px 12px;
        background: rgba(0, 0, 0, 1);
        border-radius: 6px;
    `,
    infoLine: css`
        display: flex;
        justify-content: space-between;
        font-size: 13px;
        color: #ccc;
    `,
    infoValue: css`
        color: #00d4aa;
        font-weight: 600;
    `,
    feeValue: css`
        color: #ff6b6b;
        font-weight: 700;
    `,
    netValue: css`
        color: #ff6b6b;
        font-size: 14px;
        font-weight: 700;
    `,
    amountList: css`
        display: flex;
        flex-wrap: wrap;
        background: linear-gradient(to bottom, #B80005, #710003);
        border-radius: 8px;
        padding: 4px;
        margin-bottom: 12px;
    `,
    amountItem: css`
        padding: 3px;
        flex: 0 0 12.5%;
        max-width: 12.5%;
    `,
    amountItemInner: css`
        padding: 6px 0;
        border-radius: 8px;
        box-shadow: 1px 1px 2px rgba(0, 0, 0, .4);
        cursor: pointer;
        text-align: center;
        background: rgba(0, 0, 0, .4);
        color: var(--white-color);
        font-weight: 700;
        &:active, &:hover, &.active {
            background: linear-gradient(0deg, #002287 0%, rgba(2, 126, 251, 1) 100%);
        }
    `,
    footerRow: css`
        display: flex;
        gap: 12px;
        margin-bottom: 12px;
    `,
    submitBtn: css`
        flex: 1;
        padding: 12px;
        background: linear-gradient(135deg, #00d4aa 0%, #00a884 100%);
        border: none;
        color: #000;
        font-size: 15px;
        font-weight: 700;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        &:hover {
            box-shadow: 0 4px 16px rgba(0, 212, 170, 0.4);
            transform: translateY(-1px);
        }
        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
        }
    `,
};

export default UsdtWithdraw;
